import React, { useState, useEffect } from 'react';
import {
  Container,
  Row,
  Col,
  Nav,
  Tab,
  Modal,
  Spinner,
  Form
} from 'react-bootstrap';
import { useAuth } from '../../contexts/AuthContext';
import { ProfileHeader } from '../Profile/ProfileHeader';
import TravelerProfileCard from '../Profile/TravelerProfileCard';
import TravelerProfileForm from '../Profile/TravelerProfileForm';
import FollowersList from '../Profile/FollowersList';
import ThInputTextArea from '../forms/th-iput-text-area';
import ThInputText from '../forms/th-input-text';
import ThInputSelect from '../forms/th-input-select';
import Button from '../UI/button';
import { useTravelerProfiles } from '../../shared/hooks/useTravelerProfiles';
import { useUserSettings } from '../../shared/hooks/useUserSettings';
import { useUserStats } from '../../shared/hooks/useUserStats';
import { apiClient } from '../../services/api/apiClient';
import {
  ITravelerProfile,
  CreateProfileDto,
  UpdateProfileDto,
  IPrivacySettings,
  INotificationPreferences
} from '../../types/profile.types';
import './profile-page.css';

interface FullUser {
  _id: string;
  username: string;
  email: string;
  profileImage?: string;
  bio?: string;
  firstname?: string;
  age?: number;
  socialMedia?: string[];
}

const VISIBILITY_OPTIONS = [
  { value: 'public', label: 'Public' },
  { value: 'followers', label: 'Followers Only' },
  { value: 'private', label: 'Private' }
];

const FOLLOW_PERMISSION_OPTIONS = [
  { value: 'anyone', label: 'Anyone' },
  { value: 'approved', label: 'Approved Only' },
  { value: 'nobody', label: 'Nobody' }
];

const MSG_PERMISSION_OPTIONS = [
  { value: 'anyone', label: 'Anyone' },
  { value: 'followers', label: 'Followers' },
  { value: 'nobody', label: 'Nobody' }
];

const DIGEST_OPTIONS = [
  { value: 'daily', label: 'Daily' },
  { value: 'weekly', label: 'Weekly' },
  { value: 'never', label: 'Never' }
];

interface BoolToggleProps {
  label: string;
  checked: boolean;
  onChange: (val: boolean) => void;
}

const BoolToggle: React.FC<BoolToggleProps> = ({
  label,
  checked,
  onChange
}) => (
  <Form.Check
    className='th-input-checkbox mb-2'
    type='checkbox'
    label={label}
    checked={checked}
    onChange={(e) => onChange(e.target.checked)}
  />
);

const ProfilePage: React.FC = () => {
  const { user: authUser, refreshUser } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [socialTab, setSocialTab] = useState('following');

  const [profileUser, setProfileUser] = useState<FullUser | null>(null);
  const [profileLoading, setProfileLoading] = useState(true);

  const [bio, setBio] = useState('');
  const [bioSaving, setBioSaving] = useState(false);
  const [bioError, setBioError] = useState<string | null>(null);
  const [bioSuccess, setBioSuccess] = useState(false);

  const [firstname, setFirstname] = useState('');
  const [age, setAge] = useState('');
  const [socialLinkInput, setSocialLinkInput] = useState('');
  const [socialLinks, setSocialLinks] = useState<string[]>([]);
  const [infoSaving, setInfoSaving] = useState(false);
  const [infoError, setInfoError] = useState<string | null>(null);

  const [profileModal, setProfileModal] = useState<{
    isOpen: boolean;
    editingProfile: ITravelerProfile | null;
  }>({ isOpen: false, editingProfile: null });

  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const [privacySaving, setPrivacySaving] = useState(false);
  const [privacyError, setPrivacyError] = useState<string | null>(null);
  const [notifSaving, setNotifSaving] = useState(false);
  const [notifError, setNotifError] = useState<string | null>(null);

  const {
    profiles,
    archetypes,
    loading: profilesLoading,
    error: profilesError,
    createProfile,
    updateProfile,
    deleteProfile,
    setDefault
  } = useTravelerProfiles();

  const {
    settings,
    loading: settingsLoading,
    updatePrivacy,
    updateNotifications
  } = useUserSettings();

  const { stats: tripStats } = useUserStats();

  const [privacy, setPrivacy] = useState<IPrivacySettings | null>(null);
  const [notifs, setNotifs] = useState<INotificationPreferences | null>(null);

  useEffect(() => {
    if (settings) {
      if (!privacy) setPrivacy({ ...settings.privacy });
      if (!notifs) setNotifs({ ...settings.notifications });
    }
  }, [settings]);

  useEffect(() => {
    if (!authUser) return;
    apiClient
      .get<FullUser>('/users/me')
      .then((res) => {
        setProfileUser(res.data);
        setBio(res.data.bio ?? '');
        setFirstname(res.data.firstname ?? '');
        setAge(res.data.age != null ? String(res.data.age) : '');
        setSocialLinks(res.data.socialMedia ?? []);
      })
      .catch(() => {})
      .finally(() => setProfileLoading(false));
  }, [authUser]);

  const saveBio = async () => {
    if (!profileUser) return;
    setBioSaving(true);
    setBioError(null);
    setBioSuccess(false);
    try {
      await apiClient.patch(`/users/${profileUser._id}`, { bio });
      setBioSuccess(true);
      refreshUser();
    } catch (err: any) {
      setBioError(err.response?.data?.message || 'Failed to save bio');
    } finally {
      setBioSaving(false);
    }
  };

  const savePersonalInfo = async () => {
    if (!profileUser) return;
    setInfoSaving(true);
    setInfoError(null);
    try {
      const ageNum = age ? parseInt(age, 10) : undefined;
      await apiClient.patch(`/users/${profileUser._id}`, {
        firstname: firstname.trim() || undefined,
        age: ageNum,
        socialMedia: socialLinks
      });
    } catch (err: any) {
      setInfoError(err.response?.data?.message || 'Failed to save info');
    } finally {
      setInfoSaving(false);
    }
  };

  const addSocialLink = () => {
    const trimmed = socialLinkInput.trim();
    if (trimmed && !socialLinks.includes(trimmed)) {
      setSocialLinks((prev) => [...prev, trimmed]);
    }
    setSocialLinkInput('');
  };

  const removeSocialLink = (link: string) => {
    setSocialLinks((prev) => prev.filter((l) => l !== link));
  };

  const openNewProfileModal = () => {
    setProfileModal({ isOpen: true, editingProfile: null });
  };

  const openEditProfileModal = (profile: ITravelerProfile) => {
    setProfileModal({ isOpen: true, editingProfile: profile });
  };

  const closeProfileModal = () => {
    setProfileModal({ isOpen: false, editingProfile: null });
  };

  const handleProfileSave = async (
    dto: CreateProfileDto | UpdateProfileDto
  ) => {
    if (profileModal.editingProfile) {
      await updateProfile(
        profileModal.editingProfile._id,
        dto as UpdateProfileDto
      );
    } else {
      await createProfile(dto as CreateProfileDto);
    }
    closeProfileModal();
  };

  const handleDeleteProfile = async (id: string) => {
    await deleteProfile(id);
    setDeleteConfirmId(null);
  };

  const savePrivacy = async () => {
    if (!privacy) return;
    setPrivacySaving(true);
    setPrivacyError(null);
    try {
      await updatePrivacy(privacy);
    } catch (err: any) {
      setPrivacyError(
        err.response?.data?.message || 'Failed to save privacy settings'
      );
    } finally {
      setPrivacySaving(false);
    }
  };

  const saveNotifications = async () => {
    if (!notifs) return;
    setNotifSaving(true);
    setNotifError(null);
    try {
      await updateNotifications(notifs);
    } catch (err: any) {
      setNotifError(
        err.response?.data?.message || 'Failed to save notification settings'
      );
    } finally {
      setNotifSaving(false);
    }
  };

  const setPrivacyField = <K extends keyof IPrivacySettings>(
    key: K,
    value: IPrivacySettings[K]
  ) => {
    setPrivacy((prev) => (prev ? { ...prev, [key]: value } : null));
  };

  const setNotifField = <K extends keyof INotificationPreferences>(
    key: K,
    value: INotificationPreferences[K]
  ) => {
    setNotifs((prev) => (prev ? { ...prev, [key]: value } : null));
  };

  if (!authUser) return null;

  return (
    <div className='profile-page'>
      <Container>
        <Tab.Container
          activeKey={activeTab}
          onSelect={(k) => setActiveTab(k || 'profile')}
        >
          <Nav
            variant='tabs'
            className='profile-page__tabs mb-4'
          >
            <Nav.Item>
              <Nav.Link eventKey='profile'>My Profile</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey='traveler'>Traveler Profiles</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey='social'>Following &amp; Followers</Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link eventKey='settings'>Preferences &amp; Privacy</Nav.Link>
            </Nav.Item>
          </Nav>

          <Tab.Content>
            {/* ── Tab 1: My Profile ── */}
            <Tab.Pane eventKey='profile'>
              {profileLoading ? (
                <div className='profile-page__loading'>
                  <Spinner animation='border' />
                </div>
              ) : (
                <>
                  <ProfileHeader
                    userId={authUser._id}
                    username={profileUser?.username ?? authUser.username ?? ''}
                    email={profileUser?.email ?? (authUser as any).email}
                    bio={bio}
                    profileImage={
                      profileUser?.profileImage ?? authUser.profileImage
                    }
                    showFollowButton={false}
                    tripStats={tripStats}
                  />

                  <Row className='mt-4'>
                    <Col md={6}>
                      <div className='profile-page__section'>
                        <h5 className='profile-page__section-title'>
                          About Me
                        </h5>
                        <ThInputTextArea
                          label='Bio'
                          value={bio}
                          onChange={(v: string) => setBio(v)}
                          rowCount={4}
                          required={false}
                          mb={16}
                        />
                        {bioError && (
                          <p className='text-danger small'>{bioError}</p>
                        )}
                        {bioSuccess && (
                          <p className='text-success small'>
                            Bio saved successfully.
                          </p>
                        )}
                        <Button
                          variant='primary'
                          onClick={saveBio}
                          disabled={bioSaving}
                        >
                          {bioSaving ? (
                            <Spinner
                              size='sm'
                              animation='border'
                            />
                          ) : (
                            'Save Bio'
                          )}
                        </Button>
                      </div>
                    </Col>

                    <Col md={6}>
                      <div className='profile-page__section'>
                        <h5 className='profile-page__section-title'>
                          Personal Info
                        </h5>
                        <ThInputText
                          label='First Name'
                          value={firstname}
                          onChange={(v: string) => setFirstname(v)}
                          placeholder='Your first name'
                          mb={16}
                        />
                        <div className='mb-3'>
                          <label className='form-label'>Age</label>
                          <input
                            type='number'
                            className='form-control'
                            value={age}
                            min={13}
                            max={120}
                            onChange={(e) => setAge(e.target.value)}
                          />
                        </div>
                        <div className='mb-3'>
                          <label className='form-label'>
                            Social Media Links
                          </label>
                          <div className='profile-page__chips'>
                            {socialLinks.map((link) => (
                              <span
                                key={link}
                                className='profile-page__chip'
                              >
                                <a
                                  href={link}
                                  target='_blank'
                                  rel='noopener noreferrer'
                                  className='profile-page__chip-link'
                                >
                                  {link}
                                </a>
                                <button
                                  type='button'
                                  className='profile-page__chip-remove'
                                  onClick={() => removeSocialLink(link)}
                                  aria-label='Remove link'
                                >
                                  ×
                                </button>
                              </span>
                            ))}
                          </div>
                          <div className='d-flex gap-2 mt-2'>
                            <input
                              type='url'
                              className='form-control form-control-sm'
                              value={socialLinkInput}
                              placeholder='https://...'
                              onChange={(e) =>
                                setSocialLinkInput(e.target.value)
                              }
                              onKeyDown={(e) =>
                                e.key === 'Enter' && addSocialLink()
                              }
                            />
                            <button
                              type='button'
                              className='btn btn-outline-secondary btn-sm'
                              onClick={addSocialLink}
                            >
                              Add
                            </button>
                          </div>
                        </div>
                        {infoError && (
                          <p className='text-danger small'>{infoError}</p>
                        )}
                        <Button
                          variant='primary'
                          onClick={savePersonalInfo}
                          disabled={infoSaving}
                        >
                          {infoSaving ? (
                            <Spinner
                              size='sm'
                              animation='border'
                            />
                          ) : (
                            'Save Info'
                          )}
                        </Button>
                      </div>
                    </Col>
                  </Row>
                </>
              )}
            </Tab.Pane>

            {/* ── Tab 2: Traveler Profiles ── */}
            <Tab.Pane eventKey='traveler'>
              <div className='d-flex justify-content-between align-items-center mb-3'>
                <div>
                  <h5 className='profile-page__section-title mb-1'>
                    My Traveler Profiles
                  </h5>
                  <p className='text-muted small mb-0'>
                    Profiles shape your trip and activity discovery. Primary
                    tags guide research results.
                  </p>
                </div>
                <Button
                  variant='primary'
                  onClick={openNewProfileModal}
                >
                  + New Profile
                </Button>
              </div>

              {profilesLoading && (
                <div className='profile-page__loading'>
                  <Spinner animation='border' />
                </div>
              )}
              {profilesError && (
                <p className='text-danger small'>{profilesError}</p>
              )}

              {!profilesLoading && profiles.length === 0 && (
                <div className='profile-page__empty'>
                  <p className='text-muted'>
                    You don't have any traveler profiles yet. Create one to get
                    personalized recommendations.
                  </p>
                  <Button
                    variant='primary'
                    onClick={openNewProfileModal}
                  >
                    Create Your First Profile
                  </Button>
                </div>
              )}

              <Row>
                {profiles.map((profile) => {
                  const archetype = archetypes.find(
                    (a) => a._id === profile.archetypeId
                  );
                  return (
                    <Col
                      key={profile._id}
                      md={6}
                      lg={4}
                      className='mb-3'
                    >
                      <TravelerProfileCard
                        profile={profile}
                        archetype={archetype}
                        onEdit={openEditProfileModal}
                        onDelete={(id) => setDeleteConfirmId(id)}
                        onSetDefault={setDefault}
                      />
                    </Col>
                  );
                })}
              </Row>

              <Modal
                show={profileModal.isOpen}
                onHide={closeProfileModal}
                size='lg'
                centered
              >
                <Modal.Header closeButton>
                  <Modal.Title>
                    {profileModal.editingProfile
                      ? 'Edit Traveler Profile'
                      : 'New Traveler Profile'}
                  </Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <TravelerProfileForm
                    initialProfile={profileModal.editingProfile ?? undefined}
                    archetypes={archetypes}
                    onSave={handleProfileSave}
                    onCancel={closeProfileModal}
                  />
                </Modal.Body>
              </Modal>

              <Modal
                show={!!deleteConfirmId}
                onHide={() => setDeleteConfirmId(null)}
                centered
                size='sm'
              >
                <Modal.Header closeButton>
                  <Modal.Title>Delete Profile</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                  <p>
                    Are you sure you want to delete this traveler profile? This
                    cannot be undone.
                  </p>
                </Modal.Body>
                <Modal.Footer>
                  <Button
                    variant='outline-secondary'
                    onClick={() => setDeleteConfirmId(null)}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant='danger'
                    onClick={() =>
                      deleteConfirmId && handleDeleteProfile(deleteConfirmId)
                    }
                  >
                    Delete
                  </Button>
                </Modal.Footer>
              </Modal>
            </Tab.Pane>

            {/* ── Tab 3: Following & Followers ── */}
            <Tab.Pane eventKey='social'>
              <Tab.Container
                activeKey={socialTab}
                onSelect={(k) => setSocialTab(k || 'following')}
              >
                <Nav
                  variant='pills'
                  className='mb-3'
                >
                  <Nav.Item>
                    <Nav.Link eventKey='following'>Following</Nav.Link>
                  </Nav.Item>
                  <Nav.Item>
                    <Nav.Link eventKey='followers'>Followers</Nav.Link>
                  </Nav.Item>
                </Nav>
                <Tab.Content>
                  <Tab.Pane eventKey='following'>
                    <FollowersList
                      userId={authUser._id}
                      type='following'
                    />
                  </Tab.Pane>
                  <Tab.Pane eventKey='followers'>
                    <FollowersList
                      userId={authUser._id}
                      type='followers'
                    />
                  </Tab.Pane>
                </Tab.Content>
              </Tab.Container>
            </Tab.Pane>

            {/* ── Tab 4: Preferences & Privacy ── */}
            <Tab.Pane eventKey='settings'>
              {settingsLoading ? (
                <div className='profile-page__loading'>
                  <Spinner animation='border' />
                </div>
              ) : (
                <Row>
                  <Col md={6}>
                    <div className='profile-page__section'>
                      <h5 className='profile-page__section-title'>Privacy</h5>
                      {privacy && (
                        <>
                          <ThInputSelect
                            label='Profile Visibility'
                            value={privacy.profileVisibility}
                            options={VISIBILITY_OPTIONS}
                            onChange={(v: string) =>
                              setPrivacyField(
                                'profileVisibility',
                                v as IPrivacySettings['profileVisibility']
                              )
                            }
                            mb={16}
                          />
                          <ThInputSelect
                            label='Trip Visibility'
                            value={privacy.tripVisibility}
                            options={VISIBILITY_OPTIONS}
                            onChange={(v: string) =>
                              setPrivacyField(
                                'tripVisibility',
                                v as IPrivacySettings['tripVisibility']
                              )
                            }
                            mb={16}
                          />
                          <ThInputSelect
                            label='Activity Visibility'
                            value={privacy.activityVisibility}
                            options={VISIBILITY_OPTIONS}
                            onChange={(v: string) =>
                              setPrivacyField(
                                'activityVisibility',
                                v as IPrivacySettings['activityVisibility']
                              )
                            }
                            mb={16}
                          />
                          <ThInputSelect
                            label='Who Can Follow Me'
                            value={privacy.followPermission}
                            options={FOLLOW_PERMISSION_OPTIONS}
                            onChange={(v: string) =>
                              setPrivacyField(
                                'followPermission',
                                v as IPrivacySettings['followPermission']
                              )
                            }
                            mb={16}
                          />
                          <ThInputSelect
                            label='Who Can Message Me'
                            value={privacy.messagePermission}
                            options={MSG_PERMISSION_OPTIONS}
                            onChange={(v: string) =>
                              setPrivacyField(
                                'messagePermission',
                                v as IPrivacySettings['messagePermission']
                              )
                            }
                            mb={16}
                          />
                          <p className='profile-page__subsection-label'>
                            Visibility Toggles
                          </p>
                          <BoolToggle
                            label='Show Followers List'
                            checked={privacy.showFollowersList}
                            onChange={(v) =>
                              setPrivacyField('showFollowersList', v)
                            }
                          />
                          <BoolToggle
                            label='Show Following List'
                            checked={privacy.showFollowingList}
                            onChange={(v) =>
                              setPrivacyField('showFollowingList', v)
                            }
                          />
                          <BoolToggle
                            label='Show Liked Trips'
                            checked={privacy.showLikedTrips}
                            onChange={(v) =>
                              setPrivacyField('showLikedTrips', v)
                            }
                          />
                          <BoolToggle
                            label='Allow Tagging'
                            checked={privacy.allowTagging}
                            onChange={(v) => setPrivacyField('allowTagging', v)}
                          />
                          <BoolToggle
                            label='Allow Search Indexing'
                            checked={privacy.allowSearchIndexing}
                            onChange={(v) =>
                              setPrivacyField('allowSearchIndexing', v)
                            }
                          />
                          {privacyError && (
                            <p className='text-danger small mt-2'>
                              {privacyError}
                            </p>
                          )}
                          <div className='mt-3'>
                            <Button
                              variant='primary'
                              onClick={savePrivacy}
                              disabled={privacySaving}
                            >
                              {privacySaving ? (
                                <Spinner
                                  size='sm'
                                  animation='border'
                                />
                              ) : (
                                'Save Privacy Settings'
                              )}
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  </Col>

                  <Col md={6}>
                    <div className='profile-page__section'>
                      <h5 className='profile-page__section-title'>
                        Notifications
                      </h5>
                      {notifs && (
                        <>
                          <p className='profile-page__subsection-label'>
                            Channels
                          </p>
                          <BoolToggle
                            label='Email Notifications'
                            checked={notifs.emailEnabled}
                            onChange={(v) => setNotifField('emailEnabled', v)}
                          />
                          <BoolToggle
                            label='Push Notifications'
                            checked={notifs.pushEnabled}
                            onChange={(v) => setNotifField('pushEnabled', v)}
                          />
                          <BoolToggle
                            label='In-App Notifications'
                            checked={notifs.inAppEnabled}
                            onChange={(v) => setNotifField('inAppEnabled', v)}
                          />

                          <p className='profile-page__subsection-label mt-3'>
                            Categories
                          </p>
                          <BoolToggle
                            label='Follows'
                            checked={notifs.followNotifications}
                            onChange={(v) =>
                              setNotifField('followNotifications', v)
                            }
                          />
                          <BoolToggle
                            label='Likes'
                            checked={notifs.likeNotifications}
                            onChange={(v) =>
                              setNotifField('likeNotifications', v)
                            }
                          />
                          <BoolToggle
                            label='Comments'
                            checked={notifs.commentNotifications}
                            onChange={(v) =>
                              setNotifField('commentNotifications', v)
                            }
                          />
                          <BoolToggle
                            label='Messages'
                            checked={notifs.messageNotifications}
                            onChange={(v) =>
                              setNotifField('messageNotifications', v)
                            }
                          />
                          <BoolToggle
                            label='System Alerts'
                            checked={notifs.systemNotifications}
                            onChange={(v) =>
                              setNotifField('systemNotifications', v)
                            }
                          />
                          <BoolToggle
                            label='Trip Updates'
                            checked={notifs.tripUpdateNotifications}
                            onChange={(v) =>
                              setNotifField('tripUpdateNotifications', v)
                            }
                          />
                          <BoolToggle
                            label='Recommendations'
                            checked={notifs.recommendationNotifications}
                            onChange={(v) =>
                              setNotifField('recommendationNotifications', v)
                            }
                          />

                          <p className='profile-page__subsection-label mt-3'>
                            Digest
                          </p>
                          <BoolToggle
                            label='Enable Email Digest'
                            checked={notifs.digestEnabled}
                            onChange={(v) => setNotifField('digestEnabled', v)}
                          />
                          <ThInputSelect
                            label='Digest Frequency'
                            value={notifs.digestFrequency}
                            options={DIGEST_OPTIONS}
                            onChange={(v: string) =>
                              setNotifField(
                                'digestFrequency',
                                v as INotificationPreferences['digestFrequency']
                              )
                            }
                            mb={16}
                          />

                          <p className='profile-page__subsection-label'>
                            Quiet Hours
                          </p>
                          <BoolToggle
                            label='Enable Quiet Hours'
                            checked={notifs.quietHoursEnabled}
                            onChange={(v) =>
                              setNotifField('quietHoursEnabled', v)
                            }
                          />
                          {notifs.quietHoursEnabled && (
                            <Row>
                              <Col>
                                <ThInputText
                                  label='Start'
                                  value={notifs.quietHoursStart ?? ''}
                                  onChange={(v: string) =>
                                    setNotifField('quietHoursStart', v)
                                  }
                                  placeholder='22:00'
                                  mb={16}
                                />
                              </Col>
                              <Col>
                                <ThInputText
                                  label='End'
                                  value={notifs.quietHoursEnd ?? ''}
                                  onChange={(v: string) =>
                                    setNotifField('quietHoursEnd', v)
                                  }
                                  placeholder='07:00'
                                  mb={16}
                                />
                              </Col>
                            </Row>
                          )}

                          {notifError && (
                            <p className='text-danger small mt-2'>
                              {notifError}
                            </p>
                          )}
                          <div className='mt-3'>
                            <Button
                              variant='primary'
                              onClick={saveNotifications}
                              disabled={notifSaving}
                            >
                              {notifSaving ? (
                                <Spinner
                                  size='sm'
                                  animation='border'
                                />
                              ) : (
                                'Save Notification Settings'
                              )}
                            </Button>
                          </div>
                        </>
                      )}
                    </div>
                  </Col>
                </Row>
              )}
            </Tab.Pane>
          </Tab.Content>
        </Tab.Container>
      </Container>
    </div>
  );
};

export default ProfilePage;
